import { Element, Node, Transforms } from 'slate';
import { useDocumentParams } from 'app/router/params';
import { useEditorScope } from 'app/state/contexts/EditorScopeContext';
import { useRef, useState, useEffect, useCallback } from 'react';
import { refHandler } from 'shared/lib/helper-functions';
import { useCoverageScope } from 'app/components/editor/coverage/CoverageScopeContext';
import { useStyles } from 'entities/style';
import { useUser } from 'app/state/contexts';
import { useSlate, ReactEditor, RenderElementProps } from 'slate-react';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { StyledParagraphElement as StyledParagraphElementType } from 'slate/types/slate';

import { useGetCurrentLock } from 'entities/lock';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { isInsideStyledParagraph } from '../utils/connect';
import { usePermissions } from 'app/api/permission-queries';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCoarse } from 'app/state/contexts/EditorUiContext';

export interface StyledParagraphState {
  id?: string;
  connected?: boolean;
  pendingPosition?: { top: number; left: number };
  pendingDimensions?: { width: number; height: number };
  [key: string]: unknown;
}

export const StyledParagraphElement = ({ attributes, children, element }: RenderElementProps & { isPDF?: boolean; element: StyledParagraphElementType }) => {
  const { scopeUuids, missingUuids } = useCoverageScope();
  let coverageClass = '';
  if (element.uuid) {
    if (missingUuids.has(element.uuid)) coverageClass = ' coverage-missing';
    else if (scopeUuids.has(element.uuid)) coverageClass = ' coverage-scope';
  }

  const { documentId } = useDocumentParams();
  const sectionId = useEditorScope()?.sectionId ?? '';
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const startMouseRef = useRef({ x: 0, y: 0 });
  const startPosRef = useRef({ x: 0, y: 0 });
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const { activeSection } = useEditorCoarse();
  const { data: pDocument } = useEditorDocument();
  const { sectionPermissions } = usePermissions(documentId);
  const hasWritePermission = sectionPermissions?.[sectionId] === 'WRITE';
  const { data: lock, isLoading: isLockLoading } = useGetCurrentLock(documentId, sectionId);
  const isDirty = useEditorCoarse().isDirty;
  const editor = useSlate();
  const pageStyleId = pDocument.content.design.find((d) => d.pageId === activeSection.uuid)?.styleId;
  const { selectedOrganization } = useUser();
  const sidebarContext = useSidebar();
  const { data: stylesData = [] } = useStyles({
    organization: selectedOrganization,
    documentRevisionId: pDocument.revisionId,
    styleIds: [pageStyleId],
  });
  const styledParagraph = sidebarContext?.styledParagraph;
  const setStyledParagraph = sidebarContext?.setStyledParagraph;
  const setIsSidebarOpen = sidebarContext?.setIsSidebarOpen;
  const { isTaggingMode } = useEditorMode();
  const [pageStyle] = stylesData;
  const nodeStyle = (pageStyle?.content as Record<string, Record<string, { value?: number }>>)?.[`xrp-super-id-${element?.id}`];
  const isActive = styledParagraph?.id === element.id;

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 }); // Ref to track latest position for event handlers
  const [positionInitialized, setPositionInitialized] = useState(false);
  const [hasUnsavedDrag, setHasUnsavedDrag] = useState(false);

  useEffect(() => {
    Transforms.setNodes(
      editor,
      { connected: true },
      {
        at: [], // apply at the root
        match: (node: Node) => Element.isElement(node) && node.type === 'styled-paragraph',
        mode: 'all',
      }
    );
  }, [editor]);

  useEffect(() => {
    if (isDragging) return; // Don't reset position while dragging
    const savedLeft = nodeStyle?.['left']?.value;
    const savedTop = nodeStyle?.['top']?.value;
    const hasPosition = savedLeft !== undefined && savedTop !== undefined;

    if (hasPosition) {
      setPosition({ x: savedLeft, y: savedTop });
      setPositionInitialized(true);
      setHasUnsavedDrag(false); // Position was saved, clear the flag
    } else {
      setPositionInitialized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeStyle]);

  // Initialize dimensions from saved nodeStyle or element's rendered size
  useEffect(() => {
    if (!ref.current) return;
    const savedWidth = nodeStyle?.['width']?.value;
    const savedHeight = nodeStyle?.['height']?.value;
    const rect = ref.current.getBoundingClientRect();
    dimensionsRef.current = {
      width: savedWidth !== undefined ? savedWidth : rect.width,
      height: savedHeight !== undefined ? savedHeight : rect.height,
    };
  }, [nodeStyle]);

  const isDraggable = hasWritePermission && !(styledParagraph?.connected ?? element?.connected) && !isDirty;
  const isEditable = !isDraggable && lock?.isValidForUser && lock?.isValidNow && !isLockLoading && hasWritePermission;
  const handleMouseDown = (e: React.MouseEvent) => {
    const selectedNode = isInsideStyledParagraph(editor);
    if (!selectedNode || selectedNode.id !== element.id) {
      const path = ReactEditor.findPath(editor, element);
      Transforms.select(editor, path);
      return;
    }
    if (!isDraggable || !ref.current) return;
    e.preventDefault();
    startMouseRef.current = { x: e.clientX, y: e.clientY };
    startPosRef.current = { x: position.x, y: position.y };
    const savedWidth = nodeStyle?.['width']?.value;
    const savedHeight = nodeStyle?.['height']?.value;
    const rect = ref.current.getBoundingClientRect();
    dimensionsRef.current = {
      width: savedWidth !== undefined ? savedWidth : rect.width,
      height: savedHeight !== undefined ? savedHeight : rect.height,
    };
    if (!positionInitialized) {
      setPositionInitialized(true);
    }
    setIsDragging(true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startMouseRef.current.x;
      const deltaY = e.clientY - startMouseRef.current.y;
      let newX = startPosRef.current.x + deltaX;
      let newY = startPosRef.current.y + deltaY;

      if (ref.current) {
        const pageContent = ref.current.closest('.page-content');
        if (pageContent) {
          const containerRect = pageContent.getBoundingClientRect();
          const elementRect = ref.current.getBoundingClientRect();
          const minX = 0;
          const minY = 0;
          const maxX = containerRect.width - elementRect.width;
          const maxY = containerRect.height - elementRect.height;
          newX = Math.max(minX, Math.min(newX, maxX));
          newY = Math.max(minY, Math.min(newY, maxY));
        }
      }

      positionRef.current = { x: newX, y: newY }; // Update ref for event handlers
      setPosition({ x: newX, y: newY });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setHasUnsavedDrag(true);
      setStyledParagraph &&
        setStyledParagraph((prev: StyledParagraphState | null) => ({
          ...prev,
          pendingPosition: { top: positionRef.current.y, left: positionRef.current.x },
          pendingDimensions: { width: dimensionsRef.current.width, height: dimensionsRef.current.height },
        }));
    }
    //save();
  }, [isDragging, setStyledParagraph]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      {...attributes}
      ref={refHandler(attributes, ref)}
      style={{
        overflow: 'hidden',
        cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'text',
        // Apply position via inline styles during dragging or when there are unsaved drag changes
        ...((isDragging || hasUnsavedDrag) && {
          position: 'absolute',
          top: `${position.y}px`,
          left: `${position.x}px`,
          width: `${dimensionsRef.current.width}px`,
          height: `${dimensionsRef.current.height}px`,
        }),
        ...(isDragging && { willChange: 'top, left' }),
      }}
      contentEditable={isEditable ? undefined : false}
      suppressContentEditableWarning={true}
      onMouseDown={isDraggable ? handleMouseDown : undefined}
      className={`xrp-super-id-${element.id} ${(isActive || isDragging) && isInsideStyledParagraph(editor) ? 'styled-paragraph--active' : ''}${coverageClass}`}
      onClick={
        isTaggingMode || !hasWritePermission
          ? undefined
          : () => {
              setIsSidebarOpen(7);
              // Preserve pending position/dimensions when updating the selected element
              setStyledParagraph &&
                setStyledParagraph((prev: StyledParagraphState | null) => ({
                  ...element,
                  pendingPosition: prev?.pendingPosition,
                  pendingDimensions: prev?.pendingDimensions,
                }));
            }
      }
    >
      {children}
    </div>
  );
};
